import React, { createContext, useState, useEffect, useContext } from 'react';
import { logoutUser } from '../api/logout';
import { setAccessToken } from '../api/axiosInstance';
import { jwtDecode } from 'jwt-decode';

const AuthContext = createContext();

export const AuthProvider = ({ children }) => {
  const [isAuthenticated, setIsAuthenticated] = useState(false);
  const [authLoading, setAuthLoading] = useState(true);
  const [hasTriedRefresh, setHasTriedRefresh] = useState(false);
  const [userRole, setUserRole] = useState(null);
  const [userCompany, setUserCompany] = useState(null);


  useEffect(() => {
    console.log('[AuthProvider] useEffect 실행');

    // CSRF 토큰 쿠키 체크
    const xsrfToken = document.cookie
      .split('; ')
      .find(row => row.startsWith('XSRF-TOKEN='))
      ?.split('=')[1];
    if (xsrfToken) {
      console.log('[AuthProvider] XSRF-TOKEN 쿠키 발견:', xsrfToken);
    } else {
      console.warn('[AuthProvider] XSRF-TOKEN 쿠키를 찾을 수 없습니다');
    }

    // access_token 쿠키 체크
    const tokenFromCookie = document.cookie
      .split('; ')
      .find(row => row.startsWith('access_token='))
      ?.split('=')[1];

    if (tokenFromCookie) {
      console.log('[AuthProvider] 쿠키에서 access_token 발견:', tokenFromCookie);

      setAccessToken(tokenFromCookie);
      setIsAuthenticated(true);

      let decoded = {};
      try {
        decoded = jwtDecode(tokenFromCookie);
        console.log('[AuthProvider] 디코딩된 토큰:', decoded);
      } catch (decodeError) {
        console.warn('[AuthProvider] 토큰 디코딩 실패:', decodeError);
      }
      
      // Role 저장
      const role =
        decoded.role ||
        (Array.isArray(decoded.roles) && decoded.roles.length > 0 && decoded.roles[0]) ||
        (Array.isArray(decoded.authorities) && decoded.authorities.length > 0 && decoded.authorities[0]) ||
        null;
      setUserRole(role);
      
      // Company 저장
      const company =
        decoded.company ||                // 일반 필드
        decoded.org ||                    // org 라는 이름으로 오는 경우 대비
        decoded.organization ||           // organization 으로 오는 경우 대비
        (decoded.user && decoded.user.company) ||   // nested 구조 대비
        null;
      setUserCompany(company);
      
    } else {
      console.log('[AuthProvider] 쿠키에서 access_token을 찾을 수 없습니다');
    }

    setAuthLoading(false);
    setHasTriedRefresh(true);
    console.log('[AuthProvider] authLoading false, hasTriedRefresh true 설정 완료');
  }, []);

  const logout = async () => {
    console.log('[AuthProvider] 로그아웃 시작');

    try {
      const { message } = await logoutUser();
      console.log('[AuthProvider] 로그아웃 메시지:', message);

      document.cookie = 'access_token=; path=/; max-age=0'; // 쿠키 삭제
      setAccessToken(null);
      setIsAuthenticated(false);
      setUserRole(null);

      console.log('[AuthProvider] 상태 초기화 완료');
      return message;
    } catch (e) {
      console.error('[AuthProvider] 로그아웃 API 호출 중 오류:', e);
      throw new Error('로그아웃 중 오류 발생');
    }
  };

  const logoutWithBeacon = () => {
    try {
      const url = import.meta.env.VITE_API_URL + '/api/logout';
      const blob = new Blob([], { type: 'application/json' });
      const success = navigator.sendBeacon(url, blob);
      console.log('[AuthProvider] sendBeacon 로그아웃 요청:', success ? '성공' : '실패');
    } catch (error) {
      console.error('[AuthProvider] sendBeacon 로그아웃 요청 실패:', error);
    }
  };

  return (
    <AuthContext.Provider
      value={{
        isAuthenticated,
        setIsAuthenticated,
        logout,
        logoutWithBeacon,
        authLoading,
        hasTriedRefresh,
        userRole,
        setUserRole,
        userCompany,
        setUserCompany
      }}
    >
      {children}
    </AuthContext.Provider>
  );
};

export const useAuth = () => useContext(AuthContext);
